import React from 'react';
import { useEditorRef, PlateElement } from 'platejs/react';
import { useDropdown } from '../../hooks/useDropdown';
import clsx from 'clsx';
import styles from './CodeBlockElement.module.scss';

// Curated language list — keeps bundle size tight.
// The id must match the name registered with lowlight (see usePlateEditorLogic).
const LANGUAGES: { id: string; label: string }[] = [
    { id: 'plaintext', label: 'Plain text' },
    { id: 'javascript', label: 'JavaScript' },
    { id: 'typescript', label: 'TypeScript' },
    { id: 'jsx', label: 'JSX' },
    { id: 'tsx', label: 'TSX' },
    { id: 'python', label: 'Python' },
    { id: 'json', label: 'JSON' },
    { id: 'bash', label: 'Bash / Shell' },
    { id: 'sql', label: 'SQL' },
    { id: 'css', label: 'CSS' },
    { id: 'xml', label: 'HTML / XML' },
    { id: 'yaml', label: 'YAML' },
    { id: 'java', label: 'Java' },
    { id: 'go', label: 'Go' },
];

const DEFAULT_LANG = LANGUAGES[0];

function findLang(langId: string | undefined) {
    if (!langId) return DEFAULT_LANG;
    return LANGUAGES.find((l) => l.id === langId) ?? DEFAULT_LANG;
}

interface CodeBlockElementProps {
    element: any;
    children: React.ReactNode;
    [key: string]: unknown;
}

export function CodeBlockElement({ element, children, ...rest }: CodeBlockElementProps) {
    const editor = useEditorRef();
    const { open, setOpen, ref: wrapRef } = useDropdown<HTMLDivElement>();

    const activeLang = findLang(element?.lang as string | undefined);

    const selectLang = (langId: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        try {
            const ed = editor as any;
            ed.tf.setNodes({ lang: langId }, { at: ed.api.findPath(element) });
        } catch {
            /* defensive */
        }
        setOpen(false);
    };

    return (
        <PlateElement element={element} {...(rest as any)} asChild>
            <div className={styles.codeBlock}>
                <div className={styles.langSelector} ref={wrapRef} contentEditable={false}>
                    <button
                        type="button"
                        className={styles.langTrigger}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            setOpen((o) => !o);
                        }}
                        aria-haspopup="true"
                        aria-expanded={open}
                        aria-label="Select language"
                        title="Select language"
                    >
                        {activeLang.label}
                        <span className={styles.langCaret}>▾</span>
                    </button>
                    {open && (
                        <div className={styles.langPopover} role="menu" aria-label="Pick language">
                            {LANGUAGES.map((lang) => (
                                <button
                                    key={lang.id}
                                    type="button"
                                    role="menuitem"
                                    className={clsx(styles.langOption, activeLang.id === lang.id && styles.langOptionActive)}
                                    onMouseDown={selectLang(lang.id)}
                                >
                                    {lang.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <pre className={styles.pre}>{children}</pre>
            </div>
        </PlateElement>
    );
}

interface CodeLineElementProps {
    element: any;
    children: React.ReactNode;
    [key: string]: unknown;
}

export function CodeLineElement({ element, children, ...rest }: CodeLineElementProps) {
    return (
        <PlateElement element={element} {...(rest as any)} asChild>
            <div className={styles.codeLine}>{children}</div>
        </PlateElement>
    );
}
